// Przepisy konta (SPIS R-1..R-5, docs/pwa/PLAN-ZROWNANIA.md etap 3): sekcje Biblioteka / Moje przepisy / Udostępnione
// mi, kategorie z licznikami, szukanie, sortowanie (też najpopularniejsze i najkrótszy czas - #38) i filtr ulubionych
// (#39) liczy serwer; ulubione na koncie. Biblioteka - chroniony PDF, własne - ekran szczegółów (#42). Dziedzina
// (wędzenie | trunki, docs/admin/PROJEKT.md 6.3) - przełącznik na górze listy, kategorie są per dziedzina.
import type {
  DziedzinaPrzepisu, OKReply, RecipeAmountsReply, RecipeCostReply, RecipeFormIO, RecipeFormReply, RecipeSavedReply,
  RecipeSectionOut, RecipeSectionReply, RecipesReply, RodzajPrzepisow, SortPrzepisow,
} from "../gen/kontrakt";
import { t } from "../i18n/t";
import { odczytajJson } from "./offline";
import { BladZaplecza, plik, zapytanie } from "./zaplecze";

export interface FiltrPrzepisow {
  dziedzina: DziedzinaPrzepisu;
  q: string;
  /** 0 = wszystkie, -1 = bez kategorii. */
  kategoria: number;
  sort: SortPrzepisow;
  /** Tylko ulubione (#39). */
  ulubione: boolean;
}

export const BEZ_FILTRA: FiltrPrzepisow = { dziedzina: "wedzarnia", q: "", kategoria: 0, sort: "created_desc", ulubione: false };

/** Lista: `rodzaj` - zakładka (kategorie i liczniki w jej obrębie, POROWNANIE.md 15.1). */
function parametry(f: FiltrPrzepisow, rodzaj?: RodzajPrzepisow): URLSearchParams {
  const q = new URLSearchParams({ sort: f.sort, dziedzina: f.dziedzina });
  if (rodzaj) q.set("rodzaj", rodzaj);
  if (f.q.trim()) q.set("q", f.q.trim());
  if (f.kategoria) q.set("kategoria", String(f.kategoria));
  if (f.ulubione) q.set("ulubione", "1");
  return q;
}

export const przepisy = (f: FiltrPrzepisow, zakladka?: RodzajPrzepisow) =>
  zapytanie<RecipesReply>("GET", "/api/przepisy?" + parametry(f, zakladka).toString());

export async function sekcja(rodzaj: RodzajPrzepisow, f: FiltrPrzepisow, strona: number): Promise<RecipeSectionOut> {
  const q = parametry(f, rodzaj);
  q.set("strona", String(strona));
  return (await zapytanie<RecipeSectionReply>("GET", "/api/przepisy/sekcja?" + q.toString())).sekcja;
}

/** Adres zdjęcia przepisu (#47: nr 0 = okładka) - karta, szczegóły, plan, formularz; `wersja` - po zmianie kolejności
 *  (formularz) inny adres niż w pamięci przeglądarki. */
export function zdjeciePrzepisu(id: number, nr = 0, wersja?: number): string {
  const q = new URLSearchParams();
  if (nr) q.set("nr", String(nr));
  if (wersja) q.set("v", String(wersja));
  return `/api/przepisy/${id}/zdjecie` + (q.size ? "?" + q.toString() : "");
}

export const pdfPrzepisu = (id: number) => plik(`/api/przepisy/${id}/pdf`);

/** Ilość z jednostką jak quantity() w workerze (api/przepisy_pdf.go): od 1000 g / ml - kg / l, kg i l do 0,001,
 *  reszta do 0,01, przecinek dziesiętny bez zbędnych zer. */
function iloscZJednostka(v: number, jednostka: string): string {
  const wieksza = ({ g: "kg", ml: "l" } as Record<string, string>)[jednostka];
  if (wieksza && v >= 1000) [v, jednostka] = [v / 1000, wieksza];
  const krok = jednostka === "kg" || jednostka === "l" ? 1000 : 100;
  return String(Math.round(v * krok) / krok).replace(".", ",") + " " + jednostka;
}

/** Składniki przeliczone na `kg` mięsa albo litry trunku (przelicznik „Robię: X kg” - liczy worker). Aplikacja ze
 *  sklepu bez sieci: z zapisu dla 1 kg (api/zapas.ts) przeliczone w telefonie (`na_kg`); wiersz, który umie policzyć
 *  tylko serwer (nastrzyk), mówi to wprost. */
export async function skladnikiNa(id: number, kg: number): Promise<RecipeAmountsReply> {
  const sciezka = (w: number) => `/api/przepisy/${id}/skladniki?kg=${w}`;
  try {
    return await zapytanie<RecipeAmountsReply>("GET", sciezka(kg));
  } catch (b) {
    if (!(b instanceof BladZaplecza) || (b.kod !== "siec" && b.status < 500)) throw b;
    const z = await odczytajJson<RecipeAmountsReply>(sciezka(1));
    if (!z) throw b;
    return { ...z.dane, kg, skladniki: z.dane.skladniki.map((s) => ({ ...s,
      ilosc: s.na_kg != null ? iloscZJednostka(s.na_kg * kg, s.jednostka) : s.staly ? s.ilosc : t("Przeliczy się po połączeniu z internetem") })) };
  }
}

export const kosztPrzepisu = (id: number) => zapytanie<RecipeCostReply>("GET", `/api/przepisy/${id}/koszt`);

export const ulubiony = (id: number, tak: boolean) =>
  zapytanie<OKReply>("POST", `/api/przepisy/${id}/ulubiony`, { ulubiony: tak });

// Własne przepisy i kategorie (SPIS R-4): formularz, zapis (też szkic - #45), usunięcie, udostępnienie po e-mailu.
/** Formularz: istniejący przepis ma własną dziedzinę, nowy - z `dziedzina`. */
export const formularz = (id: number, dziedzina: DziedzinaPrzepisu = "wedzarnia") =>
  zapytanie<RecipeFormReply>("GET", `/api/przepisy/formularz?id=${id}&dziedzina=${dziedzina}`);

export const zapiszPrzepis = (id: number, f: RecipeFormIO) =>
  id ? zapytanie<RecipeSavedReply>("PUT", `/api/przepisy/${id}`, f) : zapytanie<RecipeSavedReply>("POST", "/api/przepisy", f);

export const usunPrzepis = (id: number) => zapytanie<OKReply>("DELETE", `/api/przepisy/${id}`);

export const udostepnijPrzepis = (id: number, email: string) =>
  zapytanie<OKReply>("POST", `/api/przepisy/${id}/udostepnij`, { email });

export const nowaKategoria = (nazwa: string, dziedzina: DziedzinaPrzepisu) =>
  zapytanie<RecipeSavedReply>("POST", "/api/przepisy/kategorie", { nazwa, dziedzina });

export const zmienKategorie = (id: number, nazwa: string) =>
  zapytanie<OKReply>("PUT", `/api/przepisy/kategorie/${id}`, { nazwa });

export const usunKategorie = (id: number) => zapytanie<OKReply>("DELETE", `/api/przepisy/kategorie/${id}`);

export const udostepnijKategorie = (id: number, email: string) =>
  zapytanie<OKReply>("POST", `/api/przepisy/kategorie/${id}/udostepnij`, { email });
